import React from "react";

const providers = [
  {
    name: "Zum Rails",
    type: "Funding — Canada & United States",
    methods: ["Interac e-Transfer", "EFT", "PAD", "FedNow", "RTP", "ACH"],
    corridors: ["CA-KE", "CA-GH", "CA-NG", "CA-ZA", "US-KE", "US-GH", "US-NG", "US-ZA"],
    status: "active",
    icon: "🏦",
    description: "Unified North American payment rail for both CAD and USD origination. Single sandbox integration covering all ZoomPay corridors.",
    snpn: "Canada: Interac e-Transfer (instant) · US: FedNow / RTP (instant)",
    snpl: "Canada: PAD / EFT (1–2 days) · US: ACH (1–3 business days)",
    latency: "Instant (FedNow/RTP/Interac) · 1–3 days (ACH/EFT/PAD)",
    limits: "Up to $3,000 CAD / $5,000 USD per transaction",
  },
  {
    name: "Equity Bank Kenya",
    type: "Payout — Kenya",
    methods: ["M-Pesa", "Bank Transfer"],
    corridors: ["CA-KE", "US-KE"],
    status: "active",
    icon: "📱",
    description: "M-Pesa and bank disbursements across Kenya. Largest payout corridor by volume.",
    latency: "Instant (M-Pesa) · Same day (Bank)",
    limits: "Up to KES 300,000 per transaction",
  },
  {
    name: "Flutterwave",
    type: "Payout — West Africa",
    methods: ["Mobile Money", "Bank Transfer"],
    corridors: ["CA-GH", "US-GH", "CA-NG", "US-NG"],
    status: "active",
    icon: "🌍",
    description: "Multi-currency payouts across Ghana (GHS) and Nigeria (NGN) via mobile money and bank rails.",
    latency: "Instant – 1 business day",
    limits: "Up to $2,000 USD equivalent",
  },
  {
    name: "Cellulant",
    type: "Payout — East Africa",
    methods: ["Mobile Money", "Bank Transfer"],
    corridors: ["CA-UG", "CA-TZ", "CA-RW"],
    status: "coming_soon",
    icon: "📡",
    description: "Pan-African mobile money network covering Uganda, Tanzania, and Rwanda.",
    latency: "Instant – 2 business days",
    limits: "Up to $1,500 USD equivalent",
  },
  {
    name: "Peach Payments",
    type: "Payout — South Africa",
    methods: ["Bank Transfer", "EFT"],
    corridors: ["CA-ZA", "US-ZA"],
    status: "coming_soon",
    icon: "🇿🇦",
    description: "ZAR disbursements via South African banking rails.",
    latency: "Same day – 1 business day",
    limits: "Up to ZAR 50,000 per transaction",
  },
];

const statusBadge = (s) => {
  if (s === "active") return <span className="px-2 py-1 rounded text-xs font-bold bg-green-100 text-green-800">● LIVE</span>;
  return <span className="px-2 py-1 rounded text-xs font-bold bg-yellow-100 text-yellow-800">⏳ SOON</span>;
};

function ProviderCard({ p, dimmed }) {
  return (
    <div className={`border rounded-lg p-5 shadow-sm ${dimmed ? 'bg-gray-50 opacity-75' : 'bg-white'}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{p.icon}</span>
          <div>
            <p className={`font-bold ${dimmed ? 'text-gray-700' : 'text-gray-900'}`}>{p.name}</p>
            <p className="text-xs text-gray-500">{p.type}</p>
          </div>
        </div>
        {statusBadge(p.status)}
      </div>
      <p className={`text-sm mb-3 ${dimmed ? 'text-gray-500' : 'text-gray-600'}`}>{p.description}</p>
      <div className="space-y-1.5 text-xs">
        {p.snpn && (
          <div className="flex justify-between">
            <span className="text-gray-500">⚡ SNPN rail</span>
            <span className="font-medium text-blue-700">{p.snpn}</span>
          </div>
        )}
        {p.snpl && (
          <div className="flex justify-between">
            <span className="text-gray-500">📅 SNPL rail</span>
            <span className="font-medium text-yellow-700">{p.snpl}</span>
          </div>
        )}
        {!p.snpn && (
          <div className="flex justify-between">
            <span className="text-gray-500">Methods</span>
            <span className="font-medium">{p.methods.join(", ")}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-500">Corridors</span>
          <span className="font-medium">{p.corridors.join(", ")}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Settlement</span>
          <span className="font-medium">{p.latency}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Limit</span>
          <span className="font-medium">{p.limits}</span>
        </div>
      </div>
    </div>
  );
}

export default function CockpitProviders() {
  const active = providers.filter(p => p.status === "active");
  const coming = providers.filter(p => p.status === "coming_soon");

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">🔌 Payment Providers</h1>
        <p className="text-gray-500 text-sm">Funding rails and payout network partners · Sandbox integrated</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Live Providers", value: active.length, color: "bg-green-50 border-green-200" },
          { label: "Funding Countries", value: "CA + US", color: "bg-blue-50 border-blue-200" },
          { label: "Active Corridors", value: "5", color: "bg-purple-50 border-purple-200" },
          { label: "Coming Soon", value: coming.length, color: "bg-yellow-50 border-yellow-200" },
        ].map(c => (
          <div key={c.label} className={`border rounded-lg p-4 ${c.color}`}>
            <div className="text-2xl font-bold">{c.value}</div>
            <div className="text-xs text-gray-600 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Zum Rails highlight banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xl">🏦</span>
          <p className="font-bold text-blue-900">Zum Rails — Unified North American Funding Partner</p>
          <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-600 text-white">SANDBOX LIVE</span>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="bg-white rounded p-3 border border-blue-100">
            <p className="font-semibold text-blue-800 mb-1">⚡ SNPN — Send Now Pay Now</p>
            <p className="text-gray-600 text-xs">🇨🇦 Canada: Interac e-Transfer (instant settlement)</p>
            <p className="text-gray-600 text-xs">🇺🇸 United States: FedNow / RTP (instant settlement)</p>
          </div>
          <div className="bg-white rounded p-3 border border-blue-100">
            <p className="font-semibold text-yellow-800 mb-1">📅 SNPL — Send Now Pay Later</p>
            <p className="text-gray-600 text-xs">🇨🇦 Canada: PAD / EFT (1–2 business days)</p>
            <p className="text-gray-600 text-xs">🇺🇸 United States: ACH (1–3 business days)</p>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-bold text-lg mb-3">✅ Live Providers ({active.length})</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {active.map(p => <ProviderCard key={p.name} p={p} dimmed={false} />)}
        </div>
      </div>

      <div>
        <h2 className="font-bold text-lg mb-3">⏳ Coming Soon ({coming.length})</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {coming.map(p => <ProviderCard key={p.name} p={p} dimmed={true} />)}
        </div>
      </div>
    </div>
  );
}
